import { useMutation, useQuery } from "@apollo/client";
import { TextInputField, toaster } from "evergreen-ui";
import React, { useEffect, useState } from "react";
import Button from "src/Components/Button";
import ComingSoonPlaceholder from "src/Components/ComingSoonPlaceholder";
import InfoBox from "src/Components/InfoBox";
import Loader from "src/Components/Loader";
import useInput from "src/Hooks/useInput";
import { GET_COMPANY_INFO } from "src/Queries/business.shared";
import styled from "styled-components";
import {
  INSERT_BUSINESS_INFO,
  UPDATE_BUSINESS_INFO,
} from "./BusinessSettingMutations";

const Container = styled.div`
  width: 100%;
  height: 100%;
  padding: 1rem;
  padding-bottom: 10vh;
`;
const Title = styled.h1`
  ${(props) => props.theme.title};
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
`;

const InputContainer = styled.div`
  width: 100%;
  display: grid;
  -ms-grid-columns: (minmax(0, 1fr)) [1];
  grid-template-columns: repeat(1, minmax(0, 1fr));
  @media (min-width: ${(props) => props.theme.maxWidth}) {
    -ms-grid-columns: (minmax(0, 1fr)) [2];
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  grid-row-gap: 0.5rem;
  row-gap: 1.25rem;
  grid-column-gap: 1rem;
  column-gap: 1rem;
`;

const FooterWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const BusinessSettings = () => {
  const businessName = useInput("");
  const contactNo = useInput("");
  const email = useInput("");
  const address = useInput("");
  const bankName = useInput("");
  const bankAccNo = useInput("");
  const bankAccHolder = useInput("");

  const { data, loading, error, refetch } = useQuery(GET_COMPANY_INFO);

  const [isLoading, setIsLoading] = useState(false);
  const [updateBusinessProfile] = useMutation(UPDATE_BUSINESS_INFO);
  const [insertBusinessProfile] = useMutation(INSERT_BUSINESS_INFO);

  const submitBusinessProfile = (e) => {
    e.preventDefault();
    setIsLoading(true);
    let business = {
      company_name: businessName.value,
      email: email.value,
      contact_no: contactNo.value,
      address: address.value,
    };
    if (data && data.master && data.master.length > 0) {
      updateBusinessProfile({
        variables: {
          id: data.master[0].id,
          set: business,
        },
      })
        .then(() => {
          toaster.success("Business profile saved!");
          refetch().then(() => {
            setIsLoading(false);
          });
        })
        .catch((e) => {
          setIsLoading(false);
          toaster.danger(e.message);
        });
    } else {
      insertBusinessProfile({
        variables: {
          object: business,
        },
      })
        .then(() => {
          toaster.success("Information saved!");
          refetch().then(() => {
            setIsLoading(false);
          });
        })
        .catch((e) => {
          toaster.danger(e.message);
          setIsLoading(false);
        });
    }
  };

  const submitBank = (e) => {
    e.preventDefault();
    setIsLoading(true);
    let bank = {
      bank_name: bankName.value,
      bank_acc_no: bankAccNo.value,
      bank_acc_name: bankAccHolder.value,
    };
    if (data && data.master && data.master.length > 0) {
      updateBusinessProfile({
        variables: {
          id: data.master[0].id,
          set: bank,
        },
      })
        .then(() => {
          toaster.success("Banking information saved!");
          refetch().then(() => {
            setIsLoading(false);
          });
        })
        .catch((e) => {
          setIsLoading(false);
          toaster.danger(e.message);
        });
    } else {
      insertBusinessProfile({
        variables: {
          object: bank,
        },
      })
        .then(() => {
          toaster.success("Bank information saved!");
          refetch().then(() => {
            setIsLoading(false);
          });
        })
        .catch((e) => {
          toaster.danger(e.message);
          setIsLoading(false);
        });
    }
  };

  useEffect(() => {
    if (error) {
      toaster.danger(error.message);
    }
    if (
      !loading &&
      data &&
      data.master.length > 0 &&
      businessName.value === ""
    ) {
      const master = data.master[0];

      businessName.onChange(master.company_name);
      address.onChange(master.address);
      contactNo.onChange(master.contact_no);
      email.onChange(master.email);
      bankName.onChange(master.bank_name);
      bankAccNo.onChange(master.bank_acc_no);
      bankAccHolder.onChange(master.bank_acc_name);
    }
  }, [
    data,
    loading,
    error,
    businessName,
    address,
    contactNo,
    email,
    bankName,
    bankAccNo,
    bankAccHolder,
  ]);

  if (loading) {
    if (error) {
      return (
        <Container>
          <Title>Business Settings</Title>
          {error.message}
        </Container>
      );
    } else if (!data) {
      return (
        <Container>
          <Title>Business Settings</Title>
          <Loader message="Loading" />
        </Container>
      );
    }
  }
  return (
    <Container>
      <Title>Business Settings</Title>
      <InfoBox
        onSubmit={submitBusinessProfile}
        title="Business Profile"
        description="Your customers will see these information in pages that requires your contact information. (e.g. Contact Us Page)"
        content={
          <InfoWrapper>
            <div className=" group mb-4 flex flex-row items-center">
              <div className=" cursor-pointer group-hover:opacity-60 relative w-28 h-28 shadow-md rounded-full overflow-hidden">
                <img
                  alt="Company Logo"
                  src={data?.master[0]?.company_logo}
                  height="100%"
                  width="100%"
                  className=" border-solid h-full w-full border border-gray-50 object-contain object-center"
                />
                <div className=" absolute text-center text-sm w-full bottom-0 p-2 bg-gray-800 bg-opacity-40 text-white">
                  Change
                </div>
              </div>
              <div className=" flex flex-col px-4">
                <p className=" font-semibold text-base">Company Logo</p>
                <p className=" text-gray-600 text-sm">
                  This will appear on Sign In page, Loading, Menu bar.
                </p>
                <p className=" group-hover:underline text-blue-500 text-sm">
                  Change
                </p>
              </div>
            </div>
            <InputContainer>
              <TextInputField label="Business Name" {...businessName} />
              <TextInputField label="E-mail" {...email} />
              <TextInputField label="Contact No" {...contactNo} />
              <TextInputField label="Business Address" {...address} />
            </InputContainer>
          </InfoWrapper>
        }
        footer={
          <FooterWrapper>
            <Button
              loading={isLoading}
              disabled={isLoading}
              width="auto"
              label="Save"
              onClick={submitBusinessProfile}
            />
          </FooterWrapper>
        }
      />
      <InfoBox
        title="Payment Information"
        description="Your customers will see these payment details when they try to make payment directly to your company."
        onSubmit={submitBank}
        content={
          <InfoWrapper>
            <Title>Bank In Details</Title>
            <InputContainer>
              <TextInputField label="Bank Name" {...bankName} />
              <TextInputField label="Bank Account Holder" {...bankAccHolder} />
              <TextInputField label="Bank Account No" {...bankAccNo} />
            </InputContainer>
            <Title>Payment Gateway</Title>
            <ComingSoonPlaceholder />
          </InfoWrapper>
        }
        footer={
          <FooterWrapper>
            <Button
              loading={isLoading}
              disabled={isLoading}
              width="auto"
              label="Save"
              onClick={submitBank}
            />
          </FooterWrapper>
        }
      />
    </Container>
  );
};

export default BusinessSettings;
